/* PR 58 / 59 FIXES · F6 — a heat-map cell of the firm dashboard opens /risk?head=<fs_code> (dashboard/fdTheme.js TARGET.riskHead).
   The register rows carry `fs_heads` [{fs_code, name, name_ar}] from GET /risk/{eng}/items, computed on the server by the SAME
   rule the heat map counts with (backend scoping_worksheet._risk_fs_codes: response-plan codes, else the row's fs_codes —
   STEP 3 · Q3: nothing else) — the screen does not re-derive it. Kept out of pages/RiskAssessment.jsx:
     - parseHeadParam: a 4-character FS head code (the backend's _fs_head_of shape) or null = no filter;
     - a risk matches when one of its fs_heads is the code; a row without fs_heads matches nothing;
     - chip "FS head: <code> <name> ✕" (name from the rows; the code alone when no row carries it) removes the param. */
import React from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { asArray } from "@/lib/asArray";
import { fill } from "@/components/dashboard/fdTheme";

export const HEAD_PARAM = "head";

export function parseHeadParam(raw) {
  const v = String(raw ?? "").trim();
  return /^[A-Z0-9]{4}$/.test(v) ? v : null;
}

const headsOf = (r) => asArray(r?.fs_heads);

export function filterRisksByHead(risks, head) {
  const list = asArray(risks);
  if (!head) return list;
  return list.filter((r) => headsOf(r).some((h) => h?.fs_code === head));
}

export function useRiskHeadFilter(risks, lang) {
  const [params, setParams] = useSearchParams();
  const head = parseHeadParam(params.get(HEAD_PARAM));
  const rows = filterRisksByHead(risks, head);
  let name = "";
  if (head) {
    for (const r of rows) {
      const h = headsOf(r).find((x) => x?.fs_code === head);
      if (h) { name = (lang === "ar" ? h.name_ar : h.name) || h.name || ""; break; }
    }
  }
  const clear = () => setParams((prev) => { const n = new URLSearchParams(prev); n.delete(HEAD_PARAM); return n; });
  return { head, rows, name, clear };
}

export function RiskHeadChip({ head, name, clear, t }) {
  if (!head) return null;
  return (
    <div className="flex items-center gap-2 border-b px-3 py-2" data-testid="risk-head-filter">
      <span className="inline-flex items-center gap-1 rounded-full border bg-slate-50 px-3 py-1 text-xs font-medium dark:bg-slate-900/60"
            data-testid="risk-head-chip">
        {fill(t("fxD.headFilter.chip"), { code: head, name }).trim()}
        <button type="button" onClick={clear} aria-label={t("fxD.headFilter.clear")} title={t("fxD.headFilter.clear")}
                className="ms-1 rounded-full text-slate-500 hover:text-slate-900" data-testid="risk-head-chip-clear">
          <X className="h-3 w-3" />
        </button>
      </span>
    </div>
  );
}

export function RiskHeadEmptyRow({ head, shown, total, colSpan, t }) {
  if (!head || shown > 0 || total === 0) return null;
  return (
    <tr><td colSpan={colSpan} className="px-3 py-6 text-center text-muted-foreground" data-testid="risk-head-empty">
      {t("fxD.headFilter.empty")}
    </td></tr>
  );
}
